/**
 * @fileoverview Attachment picker for outgoing mail (MOR-783).
 *
 * A paperclip trigger with two sources: upload from the computer and attach
 * existing job documents. The document picker reuses the same Finder
 * (`FileSystem` + `buildLibraryItems`) as the Documents Library and the
 * vouch ledger picker, so it looks and behaves identically to the Files
 * view — folder tree, list columns (Category / Status), built-in search,
 * multi-select. Uploads are also filed as job documents under
 * Correspondence when the file type is library-compatible, so they appear
 * in the job library like inbound attachments do. Client-side limits mirror
 * convex/mail/attachmentRules.ts; the server re-validates on send.
 *
 * Attachments that reference a job document carry its `documentId`, so
 * clicking one in the chip list opens the full document detail sheet
 * (Original / Metadata / Extracted / Timeline) — the same surface as the
 * Documents Library.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { useQuery as useTanstackQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { useUploadFiles } from "@xixixao/uploadstuff/react";
import { FileText, Paperclip, Upload, X } from "lucide-react";
import { toast } from "sonner";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { Button } from "@/ui/primitives/button";
import { Spinner } from "@/ui/primitives/spinner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/composed/overlays/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/ui/composed/menus/dropdown-menu";
import {
  FileSystem,
  type FileSystemItem,
} from "@/ui/composed/file-system/file-system";
import { extractErrorMessage } from "@/utils/errors";
import { CATEGORY_LABELS } from "@/components/jobs/phases/gather/constants";
import { ProcessingStatusBadge } from "@/components/jobs/phases/gather/documents/ProcessingStatusBadge";
import { DocumentDetail } from "@/components/jobs/phases/gather/documents/DocumentDetail";
import {
  buildLibraryItems,
  type LibraryDocument,
} from "@/components/jobs/phases/gather/library/manifest";
import { FilePreviewDialog, type FilePreview } from "./FilePreviewDialog";
import type { ComposerAttachment } from "./types";

// Mirrors convex/mail/attachmentRules.ts (server is the source of truth).
const MAX_FILES = 10;
const MAX_TOTAL_BYTES = 25 * 1024 * 1024;

// Mirrors the saveDocument allowlist — other types still attach to the
// email, they just skip the job-library filing.
const LIBRARY_TYPES = new Set([
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/msword",
  "text/csv",
  "text/plain",
  "image/jpeg",
  "image/png",
  "image/tiff",
]);

function formatSize(bytes?: number): string {
  if (bytes === undefined) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function withinLimits(
  current: ComposerAttachment[],
  additions: Array<{ size: number }>,
): string | null {
  if (current.length + additions.length > MAX_FILES) {
    return `You can attach at most ${MAX_FILES} files to one email`;
  }
  const total =
    current.reduce((sum, a) => sum + (a.size ?? 0), 0) +
    additions.reduce((sum, a) => sum + a.size, 0);
  if (total > MAX_TOTAL_BYTES) {
    return "Attachments can total at most 25 MB per email";
  }
  return null;
}

/**
 * Chips for the attachments currently on the composer, with remove.
 * Clicking a chip opens the full document detail sheet when the attachment
 * is (or matches) a job document — resolved server-side by storageId so
 * older drafts work too — and otherwise a type-aware file preview dialog
 * on the signed URL.
 */
export function AttachmentChipList({
  jobId,
  attachments,
  onRemove,
}: {
  jobId: Id<"jobs">;
  attachments: ComposerAttachment[];
  onRemove?: (index: number) => void;
}) {
  const [detailDocumentId, setDetailDocumentId] =
    useState<Id<"documents"> | null>(null);
  const [filePreview, setFilePreview] = useState<FilePreview | null>(null);

  const previewInfo = useQuery(
    api.mail.queries.getAttachmentPreviewInfo,
    attachments.length > 0
      ? { jobId, storageIds: attachments.map((a) => a.storageId) }
      : "skip",
  ) as
    | Array<{
        storageId: Id<"_storage">;
        url: string | null;
        documentId: Id<"documents"> | null;
      }>
    | undefined;
  const infoByStorageId = useMemo(() => {
    const map = new Map<
      string,
      { url: string | null; documentId: Id<"documents"> | null }
    >();
    for (const info of previewInfo ?? []) {
      map.set(info.storageId, {
        url: info.url,
        documentId: info.documentId,
      });
    }
    return map;
  }, [previewInfo]);

  if (attachments.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-1.5 border-t px-4 py-2">
      {attachments.map((attachment, index) => {
        const info = infoByStorageId.get(attachment.storageId);
        const documentId = attachment.documentId ?? info?.documentId ?? null;
        const handleOpen = () => {
          if (documentId) {
            setDetailDocumentId(documentId);
          } else {
            setFilePreview({
              filename: attachment.filename,
              contentType: attachment.contentType,
              url: info?.url ?? null,
            });
          }
        };
        return (
          <span
            key={`${attachment.storageId}-${index}`}
            className="inline-flex items-center gap-1.5 rounded-md border bg-muted/40 px-2 py-1 text-xs"
          >
            <FileText className="size-3 shrink-0 text-muted-foreground" />
            <button
              type="button"
              onClick={handleOpen}
              className="max-w-[180px] truncate hover:underline"
            >
              {attachment.filename}
            </button>
            {attachment.size !== undefined && (
              <span className="text-muted-foreground">
                {formatSize(attachment.size)}
              </span>
            )}
            {onRemove && (
              <button
                type="button"
                onClick={() => onRemove(index)}
                aria-label={`Remove ${attachment.filename}`}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="size-3" />
              </button>
            )}
          </span>
        );
      })}

      <DocumentDetail
        documentId={detailDocumentId}
        isViewer
        open={detailDocumentId !== null}
        onClose={() => setDetailDocumentId(null)}
      />
      <FilePreviewDialog
        preview={filePreview}
        onClose={() => setFilePreview(null)}
      />
    </div>
  );
}

export function MailAttachmentPicker({
  jobId,
  attachments,
  onChange,
  disabled,
}: {
  jobId: Id<"jobs">;
  attachments: ComposerAttachment[];
  onChange: (next: ComposerAttachment[]) => void;
  disabled?: boolean;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [documentsOpen, setDocumentsOpen] = useState(false);
  const [uploading, setUploading] = useState(false);

  const generateUploadUrl = useMutation(
    api.jobs.gatherInformation.documents.mutations.generateUploadUrl,
  );
  const saveDocument = useMutation(
    api.jobs.gatherInformation.documents.mutations.saveDocument,
  );

  const { startUpload } = useUploadFiles(
    async () => await generateUploadUrl({ jobId }),
    {
      onUploadComplete: async (uploaded) => {
        try {
          const added: ComposerAttachment[] = [];
          for (const file of uploaded) {
            const storageId = (file.response as { storageId: Id<"_storage"> })
              .storageId;
            const contentType = file.type || "application/octet-stream";
            // File library-compatible uploads as job documents under
            // Correspondence so they show up in the library too.
            const documentId: Id<"documents"> | undefined = LIBRARY_TYPES.has(
              contentType,
            )
              ? await (async () => {
                  try {
                    return await saveDocument({
                      jobId,
                      storageId,
                      fileName: file.name,
                      fileType: contentType,
                      fileSize: file.size,
                      category: "correspondence",
                    });
                  } catch {
                    // Filing is best-effort; the attachment itself still works.
                    return undefined;
                  }
                })()
              : undefined;
            added.push({
              filename: file.name,
              contentType,
              storageId,
              size: file.size,
              documentId,
            });
          }
          onChange([...attachments, ...added]);
        } catch (err) {
          toast.error(extractErrorMessage(err, "Failed to attach file"));
        } finally {
          setUploading(false);
        }
      },
      onUploadError: (err: unknown) => {
        setUploading(false);
        toast.error(
          err instanceof Error ? err.message : "Upload failed. Try again.",
        );
      },
    },
  );

  const handleFilesChosen = (fileList: FileList | null) => {
    const files = [...(fileList ?? [])];
    if (files.length === 0) return;
    const error = withinLimits(
      attachments,
      files.map((f) => ({ size: f.size })),
    );
    if (error) {
      toast.error(error);
      return;
    }
    setUploading(true);
    void startUpload(files);
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            size="sm"
            variant="ghost"
            disabled={disabled || uploading}
            title="Attach files"
            aria-label="Attach files"
          >
            {uploading ? (
              <Spinner className="size-3.5" />
            ) : (
              <Paperclip className="size-3.5" />
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-56">
          <DropdownMenuItem
            onClick={() => fileInputRef.current?.click()}
            className="text-sm"
          >
            <Upload className="mr-1.5 size-3.5" />
            Upload from computer
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => setDocumentsOpen(true)}
            className="text-sm"
          >
            <FileText className="mr-1.5 size-3.5" />
            Attach job documents
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => {
          handleFilesChosen(e.target.files);
          e.target.value = "";
        }}
      />

      <JobDocumentPickerDialog
        jobId={jobId}
        open={documentsOpen}
        onClose={() => setDocumentsOpen(false)}
        onAttach={(picked) => {
          // Skip documents already attached (matched by storageId).
          const existing = new Set<string>(
            attachments.map((a) => a.storageId as string),
          );
          const fresh = picked.filter((d) => !existing.has(d.storageId));
          const error = withinLimits(
            attachments,
            fresh.map((d) => ({ size: d.fileSize })),
          );
          if (error) {
            toast.error(error);
            return;
          }
          onChange([
            ...attachments,
            ...fresh.map((d) => ({
              filename: d.fileName,
              contentType: d.fileType,
              storageId: d.storageId as Id<"_storage">,
              size: d.fileSize,
              documentId: d._id as Id<"documents">,
            })),
          ]);
          setDocumentsOpen(false);
        }}
      />
    </>
  );
}

/**
 * Document picker over the job library — the same Finder as the Documents
 * Library and the vouch ledger picker: folder tree, list columns, built-in
 * search, multi-select. Read-only; the only interaction is selecting
 * file(s) and confirming.
 */
function JobDocumentPickerDialog({
  jobId,
  open,
  onClose,
  onAttach,
}: {
  jobId: Id<"jobs">;
  open: boolean;
  onClose: () => void;
  onAttach: (documents: LibraryDocument[]) => void;
}) {
  const { data: folders } = useTanstackQuery({
    ...convexQuery(api.jobs.gatherInformation.folders.queries.listFolders, {
      jobId,
    }),
    enabled: open,
  });
  const { data: documents } = useTanstackQuery({
    ...convexQuery(api.jobs.gatherInformation.documents.queries.listDocuments, {
      jobId,
    }),
    enabled: open,
  });

  const { items, metaByPath } = useMemo(
    () => buildLibraryItems(folders ?? [], documents ?? []),
    [folders, documents],
  );

  const documentById = useMemo(() => {
    const map = new Map<string, LibraryDocument>();
    for (const doc of documents ?? []) {
      map.set(doc._id, doc);
    }
    return map;
  }, [documents]);

  // Document ids selected in the Finder (the confirm target).
  const [selectedDocIds, setSelectedDocIds] = useState<string[]>([]);
  useEffect(() => {
    if (open) setSelectedDocIds([]);
  }, [open]);

  const handleSelectionItemsChange = (fsItems: FileSystemItem[]) => {
    const ids: string[] = [];
    for (const item of fsItems) {
      if (item.kind !== "file") continue;
      const meta = metaByPath.get(item.path);
      if (meta?.documentId) ids.push(meta.documentId);
    }
    setSelectedDocIds(ids);
  };

  const handleAttach = () => {
    const picked = selectedDocIds
      .map((id) => documentById.get(id))
      .filter((d): d is LibraryDocument => Boolean(d));
    if (picked.length === 0) return;
    onAttach(picked);
  };

  const itemColumnsHeader = (
    <>
      <span className="w-44 shrink-0">Category</span>
      <span className="w-40 shrink-0">Status</span>
      <span className="w-24 shrink-0">Size</span>
    </>
  );

  const renderItemColumns = (item: FileSystemItem) => {
    const path =
      item.kind === "folder" && !item.path.endsWith("/")
        ? `${item.path}/`
        : item.path;
    const meta = metaByPath.get(path);
    const isFile = item.kind === "file";
    const doc = meta?.documentId
      ? documentById.get(meta.documentId)
      : undefined;

    return (
      <>
        <span className="w-44 shrink-0 truncate text-xs text-muted-foreground">
          {isFile
            ? (CATEGORY_LABELS[meta?.category ?? ""] ?? meta?.category)
            : null}
        </span>
        <span className="flex w-40 shrink-0 items-center">
          {isFile && (
            <ProcessingStatusBadge
              extractionStatus={meta?.extractionStatus}
              classificationStatus={meta?.classificationStatus}
              indexingStatus={meta?.indexingStatus}
            />
          )}
        </span>
        <span className="w-24 shrink-0 text-xs text-muted-foreground">
          {isFile ? formatSize(doc?.fileSize) : null}
        </span>
      </>
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <DialogTitle>Attach job documents</DialogTitle>
          <DialogDescription>
            Select file(s) from the job library to attach to this email.
          </DialogDescription>
        </DialogHeader>

        <div className="flex h-[460px] min-h-0 flex-col">
          <FileSystem
            items={items}
            defaultView="list"
            className="min-h-0 flex-1"
            readOnly
            itemColumnsHeader={itemColumnsHeader}
            renderItemColumns={renderItemColumns}
            onSelectionItemsChange={handleSelectionItemsChange}
          />
        </div>

        <DialogFooter className="items-center">
          <span className="mr-auto text-xs text-muted-foreground">
            {selectedDocIds.length} selected
          </span>
          <Button variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleAttach}
            disabled={selectedDocIds.length === 0}
          >
            Attach{" "}
            {selectedDocIds.length > 0
              ? `${selectedDocIds.length} file${selectedDocIds.length === 1 ? "" : "s"}`
              : "files"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
